// Meet & Assist rules, in one place.
//
// Which pickup/dropoff locations offer the service, whether normal Meet &
// Assist starts pre-selected there, whether the customer may opt back out, and
// what the opt-out choice is called. These rules were previously spelled out
// inline in the booking-confirmation, customer-edit and admin-edit screens,
// which is how they drifted apart; every screen now reads them from here.
//
// A location's policy depends on the place only, except Haneda, whose opt-out
// depends on the terminal. Callers still decide *whether* a leg has a meet
// service at all -- an airport is only a pickup point on an inbound transfer.

export const TOKYO_STATION_PLACE_ID = "ChIJC3Cf2PuLGGAROO00ukl8JwA";
export const SHINJUKU_STATION_PLACE_ID = "ChIJH7qx1tCMGGAR1f2s7PGhMhw";
export const SHINAGAWA_STATION_PLACE_ID = "ChIJdYcUblqKGGARcNsG90fDRZo";
export const HANEDA_AIRPORT_PLACE_ID = "ChIJ45IxpAtkGGAR3_hG0anDMg0";
export const NARITA_AIRPORT_PLACE_ID = "ChIJVze90XnzImARoRp3YqEpbtU";

export const MEET_ASSIST_NONE = "none";
export const MEET_ASSIST_NORMAL = "meetAssist";
export const MEET_ASSIST_VIP = "vipMeetAssist";

// Haneda Terminal 3 is the international terminal, where a greeter is
// mandatory. Terminals 1 and 2 are domestic-side and the customer may opt out.
export const HANEDA_MANDATORY_TERMINALS = ["terminal-3-international"];

// How the opt-out choice is labelled. Narita is the only location where opting
// out still means someone meets the passenger -- the driver does it instead of
// a dedicated greeter -- so it gets its own wording.
export const OPT_OUT_LABEL_NONE = "None";
export const OPT_OUT_LABEL_DRIVER_MEET = "DriverMeet";

// `isInVehicleSearchPrice` decides whether the pre-selected service is folded
// into the price on the vehicle-selection cards. Where the service is
// mandatory (Haneda T3, Tokyo, Shinjuku) the cards show the charter price only
// and `mandatoryDisclaimerKey` names the translation shown under the
// "Select vehicle" heading instead, so the customer is told up front.
const POLICIES = {
  [NARITA_AIRPORT_PLACE_ID]: {
    defaultSelection: MEET_ASSIST_NORMAL,
    isOptOutAllowed: () => true,
    optOutLabel: OPT_OUT_LABEL_DRIVER_MEET,
    isInVehicleSearchPrice: true,
  },
  // Haneda pre-selects the service, but it is only mandatory at Terminal 3 and
  // the terminal is not chosen until the booking-confirmation page. So the
  // vehicle cards leave it out; it is added once the customer is on that page.
  [HANEDA_AIRPORT_PLACE_ID]: {
    defaultSelection: MEET_ASSIST_NORMAL,
    isOptOutAllowed: (terminal) =>
      !HANEDA_MANDATORY_TERMINALS.includes(terminal),
    optOutLabel: OPT_OUT_LABEL_NONE,
    isInVehicleSearchPrice: false,
    mandatoryDisclaimerKey: "Discover.MeetAssist.Mandatory.HanedaAirport",
  },
  [TOKYO_STATION_PLACE_ID]: {
    defaultSelection: MEET_ASSIST_NORMAL,
    isOptOutAllowed: () => false,
    optOutLabel: OPT_OUT_LABEL_NONE,
    isInVehicleSearchPrice: false,
    mandatoryDisclaimerKey: "Discover.MeetAssist.Mandatory.TokyoStation",
  },
  [SHINJUKU_STATION_PLACE_ID]: {
    defaultSelection: MEET_ASSIST_NORMAL,
    isOptOutAllowed: () => false,
    optOutLabel: OPT_OUT_LABEL_NONE,
    isInVehicleSearchPrice: false,
    mandatoryDisclaimerKey: "Discover.MeetAssist.Mandatory.ShinjukuStation",
  },
  // Shinagawa offers the service but does not pre-select it, and nothing about
  // it is mandatory, so it has no disclaimer either.
  [SHINAGAWA_STATION_PLACE_ID]: {
    defaultSelection: MEET_ASSIST_NONE,
    isOptOutAllowed: () => true,
    optOutLabel: OPT_OUT_LABEL_NONE,
    isInVehicleSearchPrice: false,
  },
};

export const MEET_SERVICE_PLACE_IDS = Object.keys(POLICIES);

export const TERMINAL_SELECTION_PLACE_IDS = [
  HANEDA_AIRPORT_PLACE_ID,
  NARITA_AIRPORT_PLACE_ID,
];

// Google also returns a grouped "station area" entry next to each of these
// stations, under a different place_id. The autocomplete endpoint hides them
// (routes/utils/placeSuggestions.js -- keep the two lists in step), but a
// booking saved before that may still hold one, so it is read as the real
// station here.
export const STATION_PLACE_ID_ALIASES = {
  "ChIJu9ljKNeMGGARcFUr-NmJhAk": SHINJUKU_STATION_PLACE_ID,
  "ChIJu2cU7vuLGGART-M-fm6LD0E": TOKYO_STATION_PLACE_ID,
  "ChIJdYcUblqKGGAR_hSew3yZ5YY": SHINAGAWA_STATION_PLACE_ID,
};

// Google also lists each airport terminal as its own place ("Haneda Airport
// (HND), Terminal 3"). The autocomplete endpoint now turns those into the
// airport itself, with the terminal attached
// (routes/utils/placeSuggestions.js AIRPORT_TERMINAL_PLACES -- keep the two
// lists in step). A location saved before that may still hold a terminal
// place_id, so it is read as the airport here.
const HANEDA_TERMINAL_PLACE_IDS = [
  "ChIJPZ7mcPdjGGARJ3sJqkC4YnQ", // Terminal 1 (building)
  "ChIJ-3ZXUvdjGGAR2ErSS8e93kE", // Terminal 1 (bus stop)
  "ChIJFeAFSABjGGAR73DrIbrtnhg", // 羽田空港第1ターミナル
  "ChIJfyvLDfdjGGARBRLK4YUi42o", // 羽田空港第 1 ターミナル (bus stop)
  "ChIJjShjDvdjGGAR6pABJOme03A", // Terminal 1 Station
  "ChIJU1q2SPRjGGARCVGfybuffMA", // Terminal 2 (building)
  "ChIJ_V5enfZjGGARDyGPYj_Pa9s", // Terminal 2 (bus stop)
  "ChIJBVj-z_VjGGARRjPqPUsc-Wc", // Terminal 2 (transit)
  "ChIJe0_xnPZjGGARQU3hNOqVxk4", // Terminal 2 Station
  "ChIJEzipOlRhGGAR3-3UwuMqAnI", // Terminal 3 (building)
  "ChIJ01PIBVRhGGARR1RZERfkuvo", // 羽田空港第３ターミナル (bus stop)
  "ChIJiWT7S1FhGGARGHuLSxP_5tg", // Terminal 3 Station (transit)
  "ChIJsXfyslZhGGARbL8nFGOsncU", // Terminal 3 Station (train)
];
const NARITA_TERMINAL_PLACE_IDS = [
  "ChIJb8n8uwvzImAR-RJHLJTlTdU", // Terminal 1 (building)
  "ChIJp05sZQvzImARDTdpaDbWDLw", // Terminal 1 (bus stop)
  "ChIJCx5WYwvzImARbGx__W917s0", // Terminal 1 Station
  "ChIJ4yM7gZ3zImARsGTFuKlNSQc", // Terminal 2 (building)
  "ChIJOWLCT3bzImARSeoxn2vEu8Y", // Terminal 2 (transit)
  "ChIJYd_6WXnzImAR89wPaFU25-8", // Terminal 3 (building)
  "ChIJxVNtXHnzImARzhA0Mm9E8GM", // Terminal 3
  "ChIJRSu2Y3nzImARvxVKd7ZeKg8", // Terminal 3 (transit)
  "ChIJjVLXwXvzImARgjiMKv3jZwA", // T3
];
export const AIRPORT_TERMINAL_PLACE_ID_ALIASES = {
  ...Object.fromEntries(HANEDA_TERMINAL_PLACE_IDS.map((placeId) => [placeId, HANEDA_AIRPORT_PLACE_ID])),
  ...Object.fromEntries(NARITA_TERMINAL_PLACE_IDS.map((placeId) => [placeId, NARITA_AIRPORT_PLACE_ID])),
};

// Locations arrive from several sources: Google Places results carry
// `place_id`, saved bookings and select options carry `value`.
export const getPlaceId = (location) => {
  const placeId = location?.place_id || location?.value || null;
  return (
    STATION_PLACE_ID_ALIASES[placeId] ||
    AIRPORT_TERMINAL_PLACE_ID_ALIASES[placeId] ||
    placeId
  );
};

// The terminal a location was picked with, when the customer searched for a
// terminal ("haneda terminal 3"). Empty when there is none, or when the
// terminal alone doesn't say which one (Haneda Terminal 2 has two sides).
export const getTerminalHint = (location) => location?.terminal || "";

const NO_MEET_SERVICE = {
  offersMeetService: false,
  defaultSelection: MEET_ASSIST_NONE,
  isOptOutAllowed: true,
  optOutLabel: OPT_OUT_LABEL_NONE,
};

/**
 * The Meet & Assist policy for one leg.
 *
 * @param location  pickup or dropoff location
 * @param terminal  the selected terminal, where the location has terminals.
 *                  Before one is picked this is empty, which reads as "opt-out
 *                  still allowed" -- the customer is only locked in once they
 *                  choose a terminal that requires a greeter.
 * @returns {{offersMeetService: boolean, defaultSelection: string,
 *            isOptOutAllowed: boolean, optOutLabel: string}}
 */
export const getMeetAssistPolicy = (location, terminal = "") => {
  const policy = POLICIES[getPlaceId(location)];
  if (!policy) return NO_MEET_SERVICE;

  return {
    offersMeetService: true,
    defaultSelection: policy.defaultSelection,
    isOptOutAllowed: policy.isOptOutAllowed(terminal),
    optOutLabel: policy.optOutLabel,
  };
};

/**
 * The Meet & Assist policy for a dropoff leg.
 *
 * The defaults and the "required" rule exist for the passenger being met on
 * arrival. When the airport or station is the destination nobody needs meeting
 * there, so a dropoff is never pre-selected and never mandatory -- not even at
 * Haneda T3, Tokyo or Shinjuku. The customer can still add the service.
 *
 * For the same reason the opt-out is always a plain "None": "Driver Meet"
 * means the driver meets an arriving passenger, which only happens on a
 * Narita pickup.
 */
export const getDropoffMeetAssistPolicy = (location, terminal = "") => ({
  ...getMeetAssistPolicy(location, terminal),
  defaultSelection: MEET_ASSIST_NONE,
  isOptOutAllowed: true,
  optOutLabel: OPT_OUT_LABEL_NONE,
});

/**
 * The Meet & Assist policy for one leg on the admin booking-edit screen.
 *
 * Admins know the product rules and sometimes have to record what the customer
 * actually arranged, so nothing is mandatory here: every leg can be set to the
 * opt-out choice, at every location and terminal. Which locations offer the
 * service and what the opt-out is called still follow the customer rules, so a
 * Narita pickup keeps its "Driver Meet" label and a dropoff is a plain "None".
 *
 * @param location  pickup or dropoff location
 * @param terminal  the selected terminal, where the location has terminals
 * @param isDropoff true for the dropoff (departure) leg
 */
export const getAdminMeetAssistPolicy = (
  location,
  terminal = "",
  { isDropoff = false } = {}
) => {
  const customerPolicy = isDropoff
    ? getDropoffMeetAssistPolicy(location, terminal)
    : getMeetAssistPolicy(location, terminal);

  return { ...customerPolicy, isOptOutAllowed: true };
};

// True where normal Meet & Assist starts selected.
export const isMeetAssistPreSelected = (location) =>
  getMeetAssistPolicy(location).defaultSelection === MEET_ASSIST_NORMAL;

// True where the pre-selected service is included in the vehicle-selection
// price. Not the same as being pre-selected: Haneda pre-selects it but leaves
// it out of that price -- see POLICIES.
export const isMeetAssistInVehicleSearchPrice = (location) =>
  Boolean(POLICIES[getPlaceId(location)]?.isInVehicleSearchPrice);

/**
 * The translation key of the "Meet & Assist is mandatory here" notice for the
 * vehicle-selection screen, or null when there is nothing to say.
 *
 * Only an arrival pickup is ever mandatory (a dropoff never is -- see
 * getDropoffMeetAssistPolicy), so `hasArrival` is the caller's
 * transfer-direction check. Business accounts are exempt at Haneda T3, so they
 * get no Haneda notice.
 */
export const getMandatoryMeetAssistDisclaimerKey = ({
  pickupLocation,
  hasArrival = false,
  isBusinessUser = false,
} = {}) => {
  if (!hasArrival) return null;

  const pickupPlaceId = getPlaceId(pickupLocation);
  if (isBusinessUser && pickupPlaceId === HANEDA_AIRPORT_PLACE_ID) return null;

  return POLICIES[pickupPlaceId]?.mandatoryDisclaimerKey || null;
};

export const checkLocationUseMeetService = (location) =>
  MEET_SERVICE_PLACE_IDS.includes(getPlaceId(location));

export const checkLocationUseTerminalSelection = (location) =>
  TERMINAL_SELECTION_PLACE_IDS.includes(getPlaceId(location));

/**
 * The Meet & Assist selection a leg starts on.
 *
 * `hasLeg` is the caller's transfer-direction check: a Narita pickup only meets
 * anyone on an inbound transfer, so an outbound one gets no arrival service
 * however the place is configured.
 */
export const getDefaultMeetAssistForLeg = ({
  policy,
  hasLeg = true,
  legPrice,
}) => {
  if (!hasLeg || policy.defaultSelection !== MEET_ASSIST_NORMAL) {
    return { meetAssistName: MEET_ASSIST_NONE, meetAssistPrice: 0 };
  }

  return {
    meetAssistName: MEET_ASSIST_NORMAL,
    meetAssistPrice: legPrice?.meetAssistBasePrice || 0,
  };
};

/**
 * The two-slot `meetAssist` array a booking starts with. The slots are
 * positional and the backend indexes them directly, so both are always present
 * even when neither leg offers the service. Only the arrival leg can start
 * pre-selected -- see getDropoffMeetAssistPolicy.
 */
export const buildDefaultMeetAssistState = ({
  pickupLocation,
  dropoffLocation,
  hasArrival = true,
  hasDeparture = true,
  meetAssistPrice,
} = {}) => [
  {
    meetAssistType: "arrival",
    ...getDefaultMeetAssistForLeg({
      policy: getMeetAssistPolicy(pickupLocation),
      hasLeg: hasArrival,
      legPrice: meetAssistPrice?.pickup,
    }),
  },
  {
    meetAssistType: "departure",
    ...getDefaultMeetAssistForLeg({
      policy: getDropoffMeetAssistPolicy(dropoffLocation),
      hasLeg: hasDeparture,
      legPrice: meetAssistPrice?.dropoff,
    }),
  },
];

/**
 * Meet & Assist already included in the price shown on the vehicle-selection
 * cards: the normal service on the pickup leg, where the location pre-selects
 * it and counts it in that price (Haneda does not -- see POLICIES). A dropoff
 * never pre-selects it, so it never adds to this figure. VIP is never
 * pre-selected, so it never lands here -- upgrading to it adds to the total on
 * the booking-confirmation page.
 */
export const getPreSelectedMeetAssistTotal = (args = {}) => {
  const [arrival] = buildDefaultMeetAssistState(args);
  if (!isMeetAssistInVehicleSearchPrice(args.pickupLocation)) return 0;
  return arrival.meetAssistPrice || 0;
};

// ─── Price helpers ─────────────────────────────────────────────────
//
// Pickups and dropoffs in the small hours cost more. These mirror
// getMeetAssistPrice() in routes/controllers/Booking.js, which is what the
// booking is actually charged -- keep the two in step.

export const NIGHT_HOURS = { start: 22, end: 6 };
export const NIGHT_SURCHARGE_RATE = 1.25;

// Booking datetimes already carry Japan wall-clock time in their UTC fields, so
// the hour is read with getUTCHours rather than converted.
export const isNightHours = (dateTime) => {
  if (!dateTime) return false;
  const hour = new Date(dateTime).getUTCHours();
  return hour >= NIGHT_HOURS.start || hour < NIGHT_HOURS.end;
};

export const calculateNightSurcharge = (basePrice, isNight) =>
  isNight ? Math.ceil((basePrice || 0) * NIGHT_SURCHARGE_RATE) : basePrice || 0;

// VIP covers the first passenger, then charges per head after that.
export const calculateVipMeetAssistPrice = (basePrice, additionalPrice, totalPax) =>
  (basePrice || 0) +
  (totalPax > 1 ? (totalPax - 1) * (additionalPrice || 0) : 0);

export const createMeetAssistPriceStructure = (
  prices,
  isPickupNight,
  isDropoffNight
) => {
  const {
    meetAssistBasePrice = 0,
    vipMeetAssistBasePrice = 0,
    vipMeetAssistAdditionalPrice = 0,
  } = prices || {};

  const forLeg = (isNight) => ({
    meetAssistBasePrice: calculateNightSurcharge(meetAssistBasePrice, isNight),
    vipMeetAssistBasePrice: calculateNightSurcharge(vipMeetAssistBasePrice, isNight),
    vipMeetAssistAdditionalPrice: calculateNightSurcharge(vipMeetAssistAdditionalPrice, isNight),
  });

  return { pickup: forLeg(isPickupNight), dropoff: forLeg(isDropoffNight) };
};

/**
 * What one leg costs for a given selection. The single place that turns a
 * choice of none/meetAssist/vipMeetAssist into a number, so a re-price and a
 * fresh selection cannot disagree.
 */
export const getMeetAssistPriceForSelection = (legPrices, selection, totalPax) => {
  if (selection === MEET_ASSIST_VIP) {
    return calculateVipMeetAssistPrice(
      legPrices?.vipMeetAssistBasePrice,
      legPrices?.vipMeetAssistAdditionalPrice,
      totalPax
    );
  }
  if (selection === MEET_ASSIST_NORMAL) return legPrices?.meetAssistBasePrice || 0;
  return 0;
};
